import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

/*
Note: See app/views/shared/file_upload_field.html.haml partial for use structure.
Use: = render 'shared/file_upload_field', f: f, field_name: :video_file, label_text: 'Video file', accept: ['.zip']

This controller is for single files, triggered by the custom 'filesDropped' event in DragDropUpload::fileSelected.

- As soon as the file is selected, it will be directly uploaded via DirectUpload
- A hidden field will be added to the end of the form (overriding any previous value of the field with the same name) with the signed_id 
  of the file as the value and the name of the original file field e.g.
<input type="hidden" value="eyJfcmFpbHMiOnsiZGF0YSI6NDEwNTI2LCJwdXIiOiJibG9iX2lkIn19--cf30e9184cfd0faddf5eff39f23022ecf568a2e4" name="lms_lesson[scorm_file]">

- It will also add a hidden field used as a flag to say if the file is new or not e.g.
<input type="hidden" value="true" name="new_upload_eyJfcmFpbHMiOnsiZGF0YSI6NDEwNTI2LCJwdXIiOiJibG9iX2lkIn19--cf30e9184cfd0faddf5eff39f23022ecf568a2e4">

- It will also disable the form's submit button until all upload fields have finished uploading
- Upon form submission, the signed_id for the active storage blob will be associated with the model via a active storage attachement record
- If a validation error occurs, the hidden signed_id field is persisted and a warning is presented to the user to indicate the file, 
  although uploaded, has not yet been saved due to other form errors
*/

class Upload {
  constructor(file) {
    this.directUpload = new DirectUpload(file, "/rails/active_storage/direct_uploads", this);
  }

  process(inputTarget, progressTarget) {
    return new Promise((resolve, reject) => {
      progressTarget.innerHTML = '';
      const progressBar = this.createProgressBar();
      progressTarget.appendChild(progressBar);

      this.directUpload.create((error, blob) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          // Create a hidden input field with the signed_id
          const hiddenField = document.createElement('input');
          hiddenField.setAttribute("type", "hidden");
          hiddenField.setAttribute("value", blob.signed_id);
          hiddenField.name = inputTarget.name;

          // Append the hidden field to the form
          const form = inputTarget.closest('form');
          form.appendChild(hiddenField);

          // Set a flag to indicate a new file was uploaded
          const newFileFlag = document.createElement('input');
          newFileFlag.setAttribute("type", "hidden");
          newFileFlag.setAttribute("value", "true");
          newFileFlag.name = `new_upload_${blob.signed_id}`;
          form.appendChild(newFileFlag);

          resolve(blob);
        }
      });
    });
  }

  createProgressBar() {
    const wrapper = document.createElement("div");
    wrapper.id = `upload_${this.directUpload.id}`;
    wrapper.className = "mb-0-5";

    const progress = document.createElement("progress");
    progress.className = "w-full";
    progress.max = 100;
    progress.value = 0;

    wrapper.appendChild(progress);

    return wrapper;
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => this.updateProgress(event));
  }

  updateProgress(event) {
    const percentage = (event.loaded / event.total) * 100;
    const progress = document.querySelector(`#upload_${this.directUpload.id} progress`);
    if (progress) {
      progress.value = percentage;
    }
  }
}

export default class extends Controller {
  static targets = ["input", "progress"];
  static values = { endpoint: String }

  connect() {
    this.element.addEventListener('filesDropped', this.uploadFile.bind(this));
  }

  initialize() {
    const form = this.inputTarget.closest('form');
    this.submitButton = form.querySelector('input[type="submit"]');
    this.submitButton.dataset.uploadsInProgress = 0;
    this.updateSubmitButtonState();
  }

  uploadFile(event) {
    this.incrementUploads();
    new Upload(event.detail.files[0]).process(this.inputTarget, this.progressTarget)
      .then(() => this.decrementUploads())
      .catch(() => this.decrementUploads());
  }

  incrementUploads() {
    this.submitButton.dataset.uploadsInProgress++;
    this.updateSubmitButtonState();
  }

  decrementUploads() {
    if (this.submitButton.dataset.uploadsInProgress > 0) {
      this.submitButton.dataset.uploadsInProgress--;
    }
    this.updateSubmitButtonState();
  }

  updateSubmitButtonState() {
    if (this.submitButton) {
      this.submitButton.disabled = this.submitButton.dataset.uploadsInProgress > 0;
    }
  }
}
